import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { SmartToy } from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const ProcesoCreateModal = ({
  open,
  onClose,
  onSuccess,
  defaultGestionId = null,
  gestiones = []
}) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    gestion_id: defaultGestionId || user?.gestion_id || ''
  })

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        nombre: '',
        descripcion: '',
        gestion_id: defaultGestionId || user?.gestion_id || ''
      })
      setError(null)
      setSuccess(null)
      setStatusMessage('')
    }
  }, [open, defaultGestionId, user?.gestion_id])

  const updateStatusMessage = (message) => {
    setStatusMessage(message)
    console.log('📊 Status IA:', message)
  }

  const generateDescriptionWithAI = async () => {
    if (!formData.nombre.trim()) {
      setError('Ingrese el nombre del proceso antes de generar la descripción con IA')
      return
    }

    setAiLoading(true)
    setError(null)
    updateStatusMessage('🤖 Iniciando generación de descripción con IA...')

    try {
      // Preparar el prompt para la IA
      const prompt = `Genera una descripción detallada y profesional para un proceso de gestión educativa en una institución de educación superior colombiana.

Nombre del proceso: "${formData.nombre}"

Instrucciones:
1. La descripción debe ser específica para el proceso mencionado
2. Debe incluir objetivos, alcance y actividades principales
3. Usar terminología técnica apropiada para educación superior
- Sector: Educación Superior Pública en Colombia
- Contexto: Institución educativa con procesos de calidad
- Audiencia: Personal administrativo y académico

Estructura esperada:
1. Objetivo del proceso (1-2 párrafos)
2. Alcance y aplicabilidad
3. Actividades principales
4. Considera el contexto educativo colombiano
5. Usa lenguaje formal y técnico apropiado

Genera únicamente la descripción del proceso, sin títulos adicionales.`

      updateStatusMessage('🌐 Enviando solicitud al servicio de IA DeepSeek...')

      const response = await axios.post('/api/definiciones/generate-with-ai', {
        termino: `Proceso: ${formData.nombre}`,
        prompt: prompt
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sgc_token')}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      })

      updateStatusMessage('📦 Procesando respuesta de la IA...')

      if (response.data && response.data.definicion) {
        const generatedText = response.data.definicion.trim()
        console.log('✅ Texto generado por IA:', generatedText.substring(0, 100) + '...')

        setFormData(prev => ({
          ...prev,
          descripcion: generatedText
        }))

        updateStatusMessage('✅ Descripción generada exitosamente')
        setSuccess('Descripción generada con IA. Revise y ajuste según sea necesario.')
        setTimeout(() => {
          setStatusMessage('')
          setSuccess(null)
        }, 3000)
      } else {
        console.error('❌ Respuesta inesperada de la IA:', response.data)
        setError('No se pudo generar la descripción con IA')
      }
    } catch (error) {
      console.error('Error generando descripción con IA:', error)
      updateStatusMessage('❌ Error en la generación con IA')
      let errorMessage = 'Error al generar descripción'
      if (error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail
      } else if (error.message) {
        errorMessage += ': ' + error.message
      }
      setError(errorMessage)
      setTimeout(() => setStatusMessage(''), 3000)
    } finally {
      setAiLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!formData.nombre.trim() || !formData.descripcion.trim() || !formData.gestion_id) {
      setError('Complete todos los campos requeridos')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await axios.post('/api/procesos', formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('sgc_token')}` }
      })

      if (onSuccess) {
        onSuccess(response.data)
      }
      onClose()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Nuevo Proceso
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {statusMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {statusMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" component="label">
              Nombre del Proceso *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
              <TextField
                fullWidth
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Gestión de Matrículas"
                size="small"
              />
              <Button
                variant="outlined"
                startIcon={aiLoading ? <CircularProgress size={16} /> : <SmartToy />}
                onClick={generateDescriptionWithAI}
                disabled={aiLoading || !formData.nombre.trim()}
                size="small"
              >
                {aiLoading ? 'Generando...' : 'Generar con IA (Verbose)'}
              </Button>
            </Box>
          </Box>

          {aiLoading && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              backgroundColor: 'action.hover',
              borderRadius: 1
            }}>
              <CircularProgress size={16} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'primary.main' }}>
                La IA está analizando "{formData.nombre}" para generar una descripción detallada...
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Descripción"
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            placeholder="Descripción detallada del proceso..."
            helperText={formData.descripcion && formData.descripcion.length > 100 ? "⚠️ Descripción generada con IA. Revise y ajuste según sea necesario." : ""}
            required
          />

          <FormControl fullWidth required>
            <InputLabel>Gestión</InputLabel>
            <Select
              value={formData.gestion_id}
              label="Gestión"
              onChange={(e) => setFormData({ ...formData, gestion_id: e.target.value })}
              disabled={user?.rol === 'editor'} // Editores no pueden cambiar la gestión
            >
              {gestiones.map((gestion) => (
                <MenuItem key={gestion.id} value={gestion.id}>
                  {gestion.codigo} - {gestion.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProcesoCreateModal